import React, { useEffect, useState } from 'react';
import classes from './ResourceIcon.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tagHelpers } from '@/utils/helpers';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import t from '@/utils/Language/Clients-translations';

const determineIcon = (resource, includeTagLookup) => {
  let areaOrResourceType;
  if (includeTagLookup && resource && resource.tags && resource.tags.length > 0) {
    [areaOrResourceType] = tagHelpers.areaAndCategoryFromTag(resource.tags[0]);
  } else if (resource) {
    areaOrResourceType = resource.resource_type;
  }

  const DEFAULT_ICON_TYPE = 'fas';
  const DEFAULT_ICON_CLASS = 'rocket';
  const DEFAULT_ICON_UNICODE = '\uf135';
  const DEFAULT_ICON_COLOR = '#006782';

  if (areaOrResourceType == undefined) {
    areaOrResourceType = '';
  } else {
    areaOrResourceType = areaOrResourceType.toLowerCase();
  }

  switch (areaOrResourceType) {
    case 'urgent':
    case 'emergency':
      return {
        iconType: 'fas',
        icon: 'triangle-exclamation',
        unicode: '\uf071',
        color: '#F4A72D'
      };
      break;
    case 'family & household':
      return {
        iconType: 'fas',
        icon: 'child-reaching',
        unicode: '\uf1ae',
        color: '#9D73C7'
      };
      break;
    case 'food':
      return {
        iconType: 'fas',
        icon: 'utensils',
        unicode: '\uf2e7',
        color: '#88A0E6'
      };
      break;
    case 'health':
      return {
        iconType: 'fas',
        icon: 'plus-square',
        unicode: '\uf0fe',
        color: '#EA5D59'
      };
      break;
    case 'housing':
      return {
        iconType: 'fas',
        icon: 'home',
        unicode: '\uf015',
        color: '#5CB8E6'
      };
      break;
    case 'education':
      return {
        iconType: 'fas',
        icon: 'graduation-cap',
        unicode: '\uf19d',
        color: '#69D269'
      };
      break;
    case 'legal':
      return {
        iconType: 'fas',
        icon: 'gavel',
        unicode: '\uf0e3',
        color: '#E172AD'
      };
      break;
    case 'money':
      return {
        iconType: 'fas',
        icon: 'dollar-sign',
        unicode: '\uf155',
        color: '#5AD4C0'
      };
      break;
    case 'employment':
      return {
        iconType: 'fas',
        icon: 'briefcase',
        unicode: '\uf0b1',
        color: '#EF7F7F'
      };
      break;
    case 'one degree news': // TODO: this can be removed as of Jan 2022
      return {
        iconType: 'far',
        icon: 'newspaper',
        unicode: '\uf1ea',
        color: '#979797'
      };
      break;
    case 'all':
      return {
        iconType: 'fas',
        icon: 'circle-arrow-right',
        unicode: '\uf0a9',
        color: '#979797'
      };
      break;
    case 'more':
      return {
        iconType: 'fas',
        icon: 'ellipsis',
        unicode: '\uf141',
        color: '#EF7E7F'
      };
      break;
    case 'organization':
      return {
        iconType: 'fas',
        icon: 'sitemap',
        unicode: '\uf0e8',
        color: '#0093CC'
      };
      break;
    case 'collection':
      return {
        iconType: 'fas',
        icon: 'book', //differs from FA4
        unicode: '\uf02d',
        color: '#D5E0E2'
      };
      break;
    case 'category':
      return {
        iconType: 'fas',
        icon: 'folder-open',
        unicode: '\uf07c',
        color: '#D5E0E2'
      };
      break;
    case 'tag':
      return {
        iconType: 'fas',
        icon: 'magnifying-glass',
        unicode: '\uf002',
        color: '#979797'
      };
      break;
    case 'assessment':
      return {
        iconType: 'far',
        icon: 'comment', //differs from FA4
        unicode: '\uf086',
        color: '#979797'
      };
      break;
    case 'topicpage':
      return {
        iconType: 'fas',
        icon: 'table-columns',
        unicode: '\uf0db',
        color: '#979797'
      };
      break;
    case 'food-alter':
      return {
        iconType: 'fas',
        icon: 'basket-shopping',
        unicode: '\uf291',
        color: '#88A0E6'
      };
      break;
    case 'health-alter':
      return {
        iconType: 'fas',
        icon: 'user-doctor',
        unicode: '\uf0f0',
        color: '#EA5D58'
      };
      break;
    case 'education-alter':
      return {
        iconType: 'fas',
        icon: 'futbol',
        unicode: '\uf1e3',
        color: '#69D269'
      };
      break;
    case 'opportunity':
    default:
      return {
        iconType: DEFAULT_ICON_TYPE,
        icon: DEFAULT_ICON_CLASS,
        unicode: DEFAULT_ICON_UNICODE,
        color: DEFAULT_ICON_COLOR
      };
      break;
  }
};

// $grey in _colors.scss -- shown while the icon is unresolved, so a placeholder is
// distinguishable from a resource whose resolved icon is genuinely the default.
const LOADING_ICON_COLOR = '#CCCCCC';

const renderTooltip = (resource, ...props) => (
  <Tooltip id="button-tooltip" {...props} className={classes.tooltipText}>
    {t(resource?.areas?.[0])}
  </Tooltip>
);

const ResourceIcon = ({ resource, className = '', boxed = false, circle = false, small = false, ...props }) => {
  // The tag taxonomy (window.tags) only exists in the browser, so tag-based icons can't be
  // computed during SSR. The first render (server and hydration) must be identical, so it uses
  // only resource_type; the tag lookup is enabled after mount, making the tag-based icon a real
  // DOM update instead of a silently-ignored hydration mismatch.
  const [includeTagLookup, setIncludeTagLookup] = useState(false);
  useEffect(() => {
    setIncludeTagLookup(true);
  }, []);
  const iconConfig = determineIcon(resource, includeTagLookup);
  const iconColor = includeTagLookup ? iconConfig?.color : LOADING_ICON_COLOR;

  if (circle && iconConfig?.icon) {
    return (
      <OverlayTrigger placement="top" overlay={resource?.areas ? renderTooltip(resource) : <></>}>
        <FontAwesomeIcon
          icon={[iconConfig.iconType, iconConfig.icon]}
          mask={['fas', 'circle']}
          transform="shrink-8"
          style={{ color: iconColor }}
          className={`${classes.circleIcon} ${small ? classes.circleIconSmall : ''} ${className}`}
        />
      </OverlayTrigger>
    );
  }

  let style = { visibility: iconConfig?.icon ? 'visible' : 'hidden' };
  if (boxed) {
    style['backgroundColor'] = iconColor;
    className += ' ' + classes.boxed;
  } else {
    style['color'] = iconColor;
  }
  if (small) {
    className += ' ' + classes.small;
  }
  return (
    <div className={`${classes.resourceIcon} ${classes[iconConfig?.icon]} ${className}`} style={style}>
      {iconConfig?.icon && (
        <OverlayTrigger placement="top" overlay={resource?.areas ? renderTooltip(resource) : <></>}>
          <label>
            <FontAwesomeIcon icon={[iconConfig.iconType, iconConfig.icon]} className={classes.faIcon} />
          </label>
        </OverlayTrigger>
      )}
    </div>
  );
};

export default ResourceIcon;
